<template>
  <div class="report-page">
    <div class="filter-card">
      <div class="fc-row fc-row-primary fc-row-with-search">
        <div class="field">
          <label class="field-lbl">ช่วงวันที่</label>
          <date-picker
            v-model="dateRange"
            type="date"
            range
            format="DD MMM YY"
            :shortcuts="dateRangeShortcuts"
            :disabled-date="notAfterToday"
            placeholder="เลือกวันที่ต้องการค้นหา"
            input-class="form-control form-control-sm field-input-native"
          ></date-picker>
        </div>

        <div class="field">
          <label class="field-lbl">สถานะใบเสร็จ</label>
          <b-form-select
            v-model="receiptStatus"
            :options="receiptStatusOpt"
            size="sm"
            class="field-input-native"
          ></b-form-select>
        </div>

        <div class="field">
          <label class="field-lbl"><i class="fas fa-search"></i> ค้นหา</label>
          <b-form-input
            v-model.trim="searchTable"
            type="search"
            size="sm"
            class="field-input-native"
            placeholder="ชื่อคนไข้, DN, เลขที่ใบเสร็จ, แพทย์, รายการ หรือสาขา"
          />
        </div>

        <b-button variant="primary" class="btn-primary-lg" @click="fetchReport">
          แสดงข้อมูล
        </b-button>
      </div>

      <div class="fc-row fc-row-multi">
        <ClinicBranchMultiSelect
          :value="branchFilter"
          :options="branchOptions"
          @input="$emit('update:branchFilter', $event)"
        />
        <ClinicItemMultiSelect v-model="itemFilter" :items="itemOptions" />
      </div>
    </div>

    <div id="report-elements">
      <ClinicBranchSummary :rows="filteredList" :metrics="summaryMetrics" />

      <div class="toolbar">
        <b-pagination
          v-model="currentPage"
          :total-rows="rows"
          :per-page="perPage"
          size="sm"
          first-number
          last-number
          pills
          limit="10"
          class="mb-0"
        ></b-pagination>

        <div class="toolbar-right">
          <div class="seg" role="tablist" aria-label="มุมมอง">
            <span class="seg-lbl">มุมมอง:</span>
            <button
              type="button"
              class="seg-btn"
              :class="{ active: viewMode === 'financial' }"
              data-testid="view-mode-financial"
              @click="viewMode = 'financial'"
            >
              💰 รายได้ &amp; DF/CF
            </button>
            <button
              type="button"
              class="seg-btn"
              :class="{ active: viewMode === 'payment' }"
              data-testid="view-mode-payment"
              @click="viewMode = 'payment'"
            >
              💳 วิธีชำระ
            </button>
          </div>

          <b-dropdown size="sm" variant="outline-success" right>
            <template #button-content>
              <i class="fas fa-download"></i>
            </template>
            <b-dropdown-item @click="excelExport('ClinicItemsIncomeTable')">
              <i class="fas fa-file-excel mr-2"></i>Excel
            </b-dropdown-item>
            <b-dropdown-item @click="pdfExport('ClinicItemsIncomeTable')">
              <i class="fas fa-file-pdf mr-2"></i>PDF
            </b-dropdown-item>
          </b-dropdown>
        </div>
      </div>

      <b-table
        id="ClinicItemsIncomeTable"
        ref="ClinicItemsIncomeTable"
        class="my-0 sticky-footer report-table clinic-items-table"
        small
        sort-icon-left
        hover
        head-variant="light"
        :busy="tableBusy"
        :items="filteredList"
        :fields="fields"
        sort-by="branchName"
        :responsive="true"
        show-empty
        :per-page="perPage"
        :current-page="currentPage"
        @row-dblclicked="openReceipt"
      >
        <template #cell(index)="row">{{ row.index + 1 }}.</template>
        <template #cell(creationDt)="row">{{ formatDate(row.value) }}</template>
        <template #cell(status)="row"
          ><span v-if="row.item.cancelFlag" class="text-danger">ยกเลิก</span></template
        >
        <template #custom-foot>
          <b-tr variant="secondary" class="text-bold text-right">
            <td v-for="field in fields" :key="field.key">
              {{ isSumField(field.key) ? formatNumber(sum(field.key)) : "" }}
            </td>
          </b-tr>
        </template>
        <template #empty="">
          <p class="text-center text-muted my-2">ไม่มีข้อมูลให้แสดง</p>
        </template>
      </b-table>
    </div>

    <Dialog ref="Dialog"></Dialog>
    <Loading v-if="isLoading"></Loading>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import { eventBus } from "@/main";

import Dialog from "@/components/modal/Dialog";
import Loading from "@/components/Loading";
import ClinicBranchSummary from "@/components/reports/ClinicBranchSummary";
import ClinicBranchMultiSelect from "@/components/reports/ClinicBranchMultiSelect";
import ClinicItemMultiSelect from "@/components/reports/ClinicItemMultiSelect";
import moment from "moment";

import { exportTableToExcel, exportReportToPdf } from "@/utils/reportExport";
import { matchesSearchQuery } from "@/utils/textSearch";
import {
  branchNameOptions,
  matchesBranches,
  buildCodeLookup,
  receiptItemOption,
  uniqueItemOptions,
  selectedItemKeys,
} from "@/utils/clinicReportFilters";

const round2 = v => Math.round(v * 100) / 100;

export default {
  name: "ClinicItemsIncomeReport",
  components: {
    Dialog,
    Loading,
    ClinicBranchSummary,
    ClinicBranchMultiSelect,
    ClinicItemMultiSelect,
  },
  props: {
    // Selected branch names, shared with the other clinic report tabs. Empty = all.
    branchFilter: { type: Array, default: () => [] },
  },
  data() {
    return {
      isLoading: false,
      reportTitle: "รายงานรายรับ แยกตามรายการรักษา (คลินิก)",
      reportNameEn: "Clinic-Income-Items",

      dateRange: [],
      receiptStatus: "0",
      receiptStatusOpt: [
        { value: "", text: "ทั้งหมด" },
        { value: "0", text: "ปกติ" },
        { value: "1", text: "ยกเลิก" },
      ],
      // { type: "item", key, label } | { type: "keyword", keyword }. Empty = all items.
      itemFilter: [],
      // id -> code from the clinic master lists, loaded once after the first fetch.
      codeLookup: null,

      tableBusy: false,
      itemsList: [],

      // "financial" = รายได้ & DF/CF, "payment" = ยอดแยกตามวิธีชำระ.
      // Mirrors the per-branch ItemsIncomeReport view switch.
      viewMode: "financial",
      // Set while exporting so the Excel sheet carries the payment columns even
      // when the on-screen view is financial. null = normal rendering.
      exportTarget: null,

      // DF/CF are net of payment-method processing fees, same as the per-branch
      // report. The fee % rides along on each row's paymentAllocations because a
      // clinic-wide report has no single branch whose master list it could load.
      baseFields: [
        { key: "index", label: "" },
        { key: "branchName", label: "สาขา", sortable: true, class: "narrow-spacing" },
        { key: "creationDt", label: "วันที่", sortable: true, class: "narrow-spacing" },
        { key: "receiptNo", label: "เลขที่ใบเสร็จ", class: "narrow-spacing", tdAttr: { "data-t": "s" } },
        { key: "doctorFullName", label: "แพทย์", class: "narrow-spacing" },
        { key: "dn", label: "DN.", class: "narrow-spacing" },
        { key: "patientFullName", label: "ชื่อคนไข้", class: "narrow-spacing" },
        { key: "itemCode", label: "รหัส", class: "narrow-spacing", tdAttr: { "data-t": "s" } },
        { key: "itemName", label: "รายการรักษา", class: "narrow-spacing" },
        { key: "price", label: "ค่ารักษา", class: "text-right", formatter: "formatNumber" },
        { key: "finalDiscount", label: "ส่วนลด", class: "text-right", formatter: "formatNumber" },
        { key: "amount", label: "สุทธิ", class: "text-right", formatter: "formatNumber" },
        { key: "paid", label: "ชำระ", class: "text-right", formatter: "formatNumber" },
        { key: "paidDfNet", label: "DF", class: "text-right", formatter: "formatNumber" },
        { key: "paidCfNet", label: "CF", class: "text-right", formatter: "formatNumber" },
        { key: "overdue", label: "ค้างชำระ", class: "text-right text-danger", formatter: "formatNumber" },
        { key: "status", label: "" },
      ],
      baseSumKeys: [
        "price",
        "finalDiscount",
        "amount",
        "paid",
        "processingFeeBaht",
        "paidDfNet",
        "paidCfNet",
        "overdue",
      ],

      searchTable: "",
      currentPage: 1,
      perPage: 100,
    };
  },
  computed: {
    ...mapGetters({
      getReportDateRange: "moduleReport/getReportDateRange",
      dateRangeShortcuts: "moduleMaster/getDateRangeShortcuts",
      clinicInfo: "moduleBranch/getClinicInfo",
    }),
    showFinancial() {
      return this.viewMode === "financial";
    },
    showPayment() {
      return this.viewMode === "payment";
    },
    isExcelExport() {
      return this.exportTarget === "excel";
    },
    hasAnyFee() {
      return this.filteredList.some(r => parseFloat(r.processingFeeBaht) > 0);
    },
    // Payment methods are per-branch master rows, so "เงินสด" is a different id
    // in every branch. A clinic roll-up therefore keys its columns by NAME,
    // collapsing the same method across branches into one column.
    paymentMethodColumns() {
      const names = [
        ...new Set(
          this.itemsList.flatMap(i =>
            (i.paymentAllocations || []).map(a => a.name).filter(Boolean)
          )
        ),
      ].sort((a, b) => String(a).localeCompare(String(b), "th"));
      // Field keys must be plain identifiers: a method name can contain a dot,
      // which b-table would read as a nested path.
      return names.map((name, idx) => ({ key: `pmt${idx}`, name }));
    },
    paymentKeyByName() {
      return new Map(this.paymentMethodColumns.map(c => [c.name, c.key]));
    },
    paymentFields() {
      return this.paymentMethodColumns.map(c => ({
        key: c.key,
        label: c.name,
        class: "text-right grp-pay",
        thClass: "narrow-spacing",
        formatter: v => this.formatNumber(v || 0),
      }));
    },
    feeField() {
      return {
        key: "processingFeeBaht",
        label: "ค่าธรรมเนียม",
        class: "text-right",
        formatter: "formatNumber",
      };
    },
    // Financial view: ...ชำระ | ค่าธรรมเนียม | DF | CF | ค้างชำระ.
    // Payment view: the fee/DF/CF trio gives way to one column per วิธีชำระ.
    // The Excel export always carries both sets.
    fields() {
      const showMoney = this.showFinancial || this.isExcelExport;
      const out = [];
      for (const f of this.baseFields) {
        const isFeeGroup = f.key === "paidDfNet" || f.key === "paidCfNet";
        if (isFeeGroup && !showMoney) continue;
        if (f.key === "paidDfNet" && this.hasAnyFee) out.push(this.feeField);
        if (f.key === "status" && (this.showPayment || this.isExcelExport)) {
          out.push(...this.paymentFields);
        }
        out.push(f);
      }
      return out;
    },
    sumFieldKeys() {
      return [
        ...this.baseSumKeys,
        ...this.paymentMethodColumns.map(c => c.key),
      ];
    },
    summaryMetrics() {
      if (this.showPayment) {
        return [
          { key: "paid", label: "ชำระ" },
          ...this.paymentMethodColumns.map(c => ({ key: c.key, label: c.name })),
        ];
      }
      return [
        { key: "price", label: "ค่ารักษา" },
        { key: "finalDiscount", label: "ส่วนลด" },
        { key: "amount", label: "สุทธิ" },
        { key: "paid", label: "ชำระ" },
        ...(this.hasAnyFee
          ? [{ key: "processingFeeBaht", label: "ค่าธรรมเนียม" }]
          : []),
        { key: "paidDfNet", label: "DF" },
        { key: "paidCfNet", label: "CF" },
        { key: "overdue", label: "ค้างชำระ" },
      ];
    },
    branchOptions() {
      return branchNameOptions(this.itemsList);
    },
    itemOptions() {
      return uniqueItemOptions(this.itemsList.map(r => receiptItemOption(r, this.codeLookup)));
    },
    rowsWithCode() {
      return this.itemsList.map(r => {
        const option = receiptItemOption(r, this.codeLookup);
        return { ...r, _itemKey: option ? option.key : null, itemCode: option ? option.code : "" };
      });
    },
    filteredList() {
      const itemKeys = selectedItemKeys(this.itemFilter, this.itemOptions);
      let data = this.rowsWithCode;
      if (this.receiptStatus === "0") {
        data = data.filter(i => !i.cancelFlag);
      } else if (this.receiptStatus === "1") {
        data = data.filter(i => i.cancelFlag);
      }
      return data
        .filter(i => matchesBranches(i.branchName, this.branchFilter))
        .filter(i => !this.itemFilter.length || itemKeys.has(i._itemKey))
        .filter(i =>
          matchesSearchQuery(i, this.searchTable, [
            "receiptNo",
            "patientFullName",
            "doctorFullName",
            "itemName",
            "itemCode",
            "dn",
            "branchName",
          ])
        )
        .slice(0)
        .sort((a, b) => {
          const byBranch = String(a.branchName || "").localeCompare(
            String(b.branchName || ""),
            "th"
          );
          if (byBranch !== 0) return byBranch;
          return moment(a.creationDt).valueOf() - moment(b.creationDt).valueOf();
        })
        .map(i => {
          const allocations = i.paymentAllocations || [];

          // One numeric cell per วิธีชำระ column, zero-filled so both the sum
          // row and the per-branch summary see every column on every row.
          const byMethod = Object.fromEntries(
            this.paymentMethodColumns.map(c => [c.key, 0])
          );
          let feeBaht = 0;
          for (const a of allocations) {
            const key = this.paymentKeyByName.get(a.name);
            const amount = parseFloat(a.amount) || 0;
            if (key) byMethod[key] = round2(byMethod[key] + amount);
            feeBaht += (amount * (parseFloat(a.processingFee) || 0)) / 100;
          }
          const processingFeeBaht = round2(feeBaht);

          // Same netting as the per-branch report: the fee is split between DF
          // and CF in proportion to their shares of the paid amount.
          const df = Number(i.paidDf) || 0;
          const cf = Number(i.paidCf) || 0;
          const feeBase = df + cf;
          const feeDf = feeBase > 0 ? (processingFeeBaht * df) / feeBase : 0;
          const feeCf = feeBase > 0 ? (processingFeeBaht * cf) / feeBase : 0;

          return {
            ...i,
            price: Number(i.price) || 0,
            finalDiscount: Number(i.finalDiscount) || 0,
            amount: Number(i.amount) || 0,
            paid: Number(i.paid) || 0,
            paidDf: df,
            paidCf: cf,
            processingFeeBaht,
            paidDfNet: round2(df - feeDf),
            paidCfNet: round2(cf - feeCf),
            ...byMethod,
            overdue: Number(i.overdue) || 0,
            _rowVariant: i.cancelFlag ? "secondary" : null,
          };
        });
    },
    startDt() {
      return this.dateRange[0] ? moment(this.dateRange[0]).startOf("day").format() : "";
    },
    endDt() {
      return this.dateRange[1] ? moment(this.dateRange[1]).endOf("day").format() : "";
    },
    today() {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return today;
    },
    rows() {
      return this.filteredList.length;
    },
  },
  watch: {
    searchTable() {
      this.currentPage = 1;
    },
    branchFilter() {
      this.currentPage = 1;
    },
    itemFilter() {
      this.currentPage = 1;
    },
    receiptStatus() {
      this.currentPage = 1;
    },
  },
  methods: {
    ...mapActions({
      fetchClinicReportReceiptItem: "moduleReport/fetchClinicReportReceiptItem",
      fetchClinicMasterCodes: "moduleReport/fetchClinicMasterCodes",
      setStateReportDateRange: "moduleReport/setStateReportDateRange",
    }),
    moment() {
      return moment();
    },
    formatDate(date) {
      return date ? moment(date).locale("th").format("D MMM YY") : "";
    },
    formatNumber(value) {
      if (value === null || value === undefined || isNaN(value)) return "";
      return Number(value).toLocaleString("th-TH", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    isSumField(key) {
      return this.sumFieldKeys.includes(key);
    },
    sum(key) {
      if (!this.isSumField(key)) return null;
      return this.filteredList.reduce((accum, item) => {
        const data = item[key];
        if (typeof data != "number") return accum;
        return Math.round((accum + data) * 100) / 100;
      }, 0);
    },
    fetchReport() {
      if (this.startDt === "" || this.endDt === "") return;
      this.setStateReportDateRange(this.dateRange);

      this.isLoading = true;
      this.fetchClinicReportReceiptItem({
        clinicUrl: this.$route.params.clinicUrl,
        startDt: this.startDt,
        endDt: this.endDt,
        // Fetch every status; the status dropdown filters client-side.
        receiptStatus: "",
      })
        .then(res => {
          this.itemsList = res.data.report;
          this.loadMasterCodes(res.data.branches);
        })
        .catch(err => {
          console.error(err);
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    // Codes only label the rows; on failure the report still works, keyed by master id.
    async loadMasterCodes(branches) {
      if (this.codeLookup || !branches || !branches.length) return;
      try {
        const { treats, items } = await this.fetchClinicMasterCodes({
          clinicUrl: this.$route.params.clinicUrl,
          branchUrl: branches[0].code,
        });
        this.codeLookup = buildCodeLookup(treats, items);
      } catch (err) {
        console.error(err);
        this.$refs.Dialog.showToast("danger", "ไม่สามารถโหลดรหัสรายการรักษาได้");
      }
    },
    async excelExport(id) {
      // Excel gets every column — financial AND วิธีชำระ — regardless of the
      // view on screen. PDF stays WYSIWYG: it exports the current view.
      this.exportTarget = "excel";
      this.perPage = null;
      try {
        await this.$nextTick();
        exportTableToExcel({
          tableId: id,
          reportTitle: this.reportTitle,
          reportNameEn: this.reportNameEn,
          dateFrom: this.dateRange[0],
          dateTo: this.dateRange[1],
          clinicName: this.clinicInfo && this.clinicInfo.name,
        });
      } finally {
        this.perPage = 100;
        this.exportTarget = null;
      }
    },
    async pdfExport(id) {
      this.perPage = null;
      await this.$nextTick();
      exportReportToPdf({
        tableId: id,
        reportTitle: this.reportTitle,
        reportNameEn: this.reportNameEn,
        dateFrom: this.dateRange[0],
        dateTo: this.dateRange[1],
        clinicName: this.clinicInfo && this.clinicInfo.name,
      });
      this.perPage = 100;
    },
    notAfterToday(date) {
      return date > new Date(new Date().setHours(0, 0, 0, 0));
    },
    openReceipt(row) {
      eventBus.$emit("openReceipt", row);
    },
  },
  mounted() {
    if (this.getReportDateRange[0] && this.getReportDateRange[1]) {
      this.dateRange = this.getReportDateRange;
    } else {
      this.dateRange = [this.today, this.today];
    }
  },
};
</script>

<style>
/* Payment-method columns get the same green tint as the per-branch report.
   Not scoped: these cells are rendered inside b-table. */
.clinic-items-table thead .grp-pay {
  background: #f3faf6 !important;
}
.clinic-items-table tbody td.grp-pay {
  background: #f9fdfb;
}
</style>
